import React, { useMemo } from 'react';
import { useIntl, useHistory } from 'umi';
import { DropdownBox, List, Popover } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import TipContext from '@/pages/home/components/TipContext';
import { EntityTypeMsg } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import useGenreSelector, { findGenre } from '@/pages/intelligence/hooks/useGenreSelector';
import TextBubbles from '@/pages/home/components/TextBubbles';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useGenresIntl from '@intelligence/hooks/useGenresIntl';
import GenreFeedback from '@intelligence/pages/TopCharts/components/DetailsPage/GenreFeedback';
import { isNotEmpty } from '@intelligence/v3/utils/common';
import { getGenreNameI18n } from '@intelligence/v3/utils/genre';
import type { IGenreSource, BasicInfoCompany } from '@intelligence/types/common';
import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';

interface IBasicInfo {
  is_iegg: boolean;
  is_sensortower: boolean;
  is_annie: boolean;
  is_appmagic?: boolean;
  genre_source?: string;
  [key: string]: unknown;
}

const hasAppMagicGenreInfo = (payload?: IRecord | null): boolean => {
  return Boolean(payload?.super_genre || payload?.major_category);
};

const calcGenreSourceByBasicInfo = (basicInfo?: IBasicInfo | null, gameDetails?: IRecord | null): string => {
  if (basicInfo?.is_appmagic || basicInfo?.genre_source === 'appmagic') {
    return 'appmagic';
  }
  if (basicInfo?.is_iegg) {
    return 'iegg';
  }
  if (basicInfo?.is_sensortower) {
    return 'sensortower';
  }
  if (basicInfo?.is_annie) {
    return 'annie';
  }
  if (hasAppMagicGenreInfo(gameDetails) || hasAppMagicGenreInfo(basicInfo)) {
    return 'appmagic';
  }
  return '';
};

export default function CompanyAndGenre(props: any) {
  const { basicInfo = {}, gameDetails = {}, entityType = 'mobile', domeHeight, genreOptionsMap } = props;
  const { company = [], dev_company = [] } = basicInfo;
  // 涉及品类这一部分的内容，pc、console联合id游戏 entityType按combine处理（使用独立释义文案），其余按实际entityType处理
  const entityTypeForGenres = entityType;
  const { locale, formatMessage } = useIntl() as any; // 中英切换方法
  const history = useHistory();
  const { genreOptions } = useGenreSelector({ entityType });
  const { resetFilterExtend, resetDetailFilterExtend } = useFilterExtend({});
  const genresDataFormatMessage = useGenresIntl();

  // 联调More功能使用
  const companyNew = company?.filter(
    (item: BasicInfoCompany) => item.name !== '' && item.uuid !== '',
  );
  const dev_companyNew = dev_company?.filter(
    (item: BasicInfoCompany) => item.name !== '' && item.uuid !== '',
  );

  const companyList = useMemo<any>(() => {
    let companyObj = {};
    const companyNum = companyNew?.length > 4 ? 3 : 4;
    const companyNormal = companyNew?.slice(0, companyNum);
    let companyMore = null;
    if (companyNew?.length > companyNum) {
      companyMore = companyNew?.slice(companyNum, companyNew?.length);
    }
    companyObj = {
      list: companyNormal,
      more: companyMore,
    };
    let dev_companyObj = {};
    const dev_companyNum = dev_companyNew?.length > 4 ? 3 : 4;
    const dev_companyNormal = dev_companyNew?.slice(0, dev_companyNew?.length > 4 ? 3 : 4);
    let dev_companyMore = null;
    if (dev_companyNew?.length > dev_companyNum) {
      dev_companyMore = dev_companyNew?.slice(dev_companyNum, dev_companyNew?.length);
    }
    dev_companyObj = {
      list: dev_companyNormal,
      more: dev_companyMore,
    };
    return [companyObj, dev_companyObj];
  }, [companyNew, dev_companyNew]);

  const genreSource = calcGenreSourceByBasicInfo(basicInfo, gameDetails);
  let sourceMainGenre = basicInfo?.genre || '';
  let sourceSubGenre = basicInfo?.sub_genre || '';

  if (genreSource === 'appmagic') {
    sourceMainGenre = gameDetails?.super_genre || basicInfo?.super_genre || '';
    sourceSubGenre = gameDetails?.major_category || basicInfo?.major_category || '';
  }

  const genreList = useMemo<any>(() => {
    const genreTemp = sourceMainGenre?.length > 0 ? sourceMainGenre.split('|') : [];
    const subGenreTemp = sourceSubGenre?.length > 0 ? sourceSubGenre.split('|') : [];

    // 联调More功能使用
    // const genreNew = [...genreTemp, ...genreTemp, ...genreTemp, ...genreTemp];
    // const sub_genreNew = [...subGenreTemp, ...subGenreTemp, ...subGenreTemp, ...subGenreTemp];
    const genreNew = genreTemp;
    const sub_genreNew = subGenreTemp;

    let genreTempObj = {};
    const genreTempNum = genreNew?.length > 4 ? 3 : 4;
    const genreTempNormal = genreNew?.slice(0, genreTempNum);
    let genreTempMore = null;
    if (genreNew?.length > genreTempNum) {
      genreTempMore = genreNew?.slice(genreTempNum, genreNew?.length);
    }
    genreTempObj = {
      list: genreTempNormal,
      more: genreTempMore,
    };

    let subGenreTempObj = {};
    const subGenreTempNum = sub_genreNew?.length > 4 ? 3 : 4;
    const subGenreTempNormal = sub_genreNew?.slice(0, sub_genreNew?.length > 4 ? 3 : 4);
    let subGenreTempMore = null;
    if (sub_genreNew?.length > subGenreTempNum) {
      subGenreTempMore = sub_genreNew?.slice(subGenreTempNum, sub_genreNew?.length);
    }
    subGenreTempObj = {
      list: subGenreTempNormal,
      more: subGenreTempMore,
    };
    return [genreTempObj, subGenreTempObj];
  }, [sourceMainGenre, sourceSubGenre]);

  const firstMainGenre = genreList?.[0]?.list?.[0];
  const sensorTowerGenreOptions = useMemo<IOptionWithChildren[]>(() => {
    if (genreSource !== 'sensortower') {
      return [];
    }

    return genreOptionsMap?.sensortower || [];
  }, [genreOptionsMap, genreSource]);

  const getAppMagicGenreName = (genre: string): string => {
    if (!genreOptionsMap) {
      return genre;
    }

    return getGenreNameI18n({
      locale,
      genreOptionsMap,
      genreSource: 'appmagic',
      genreValue: genre,
    });
  };

  const genreFormatMessage = (payload: {
    genreSource: IGenreSource;
    isMainGenre: boolean;
    firstMainGenre: string;
    genre: string;
  }): string => {
    const { genreSource, isMainGenre, firstMainGenre, genre } = payload || {};
    if (genreSource === 'appmagic') {
      return getAppMagicGenreName(genre);
    }

    const { mainGenre, subGenre } = genresDataFormatMessage({
      genreSource,
      mainGenre: isMainGenre ? genre : firstMainGenre,
      subGenre: isMainGenre ? '' : genre,
    });
    return (isMainGenre ? mainGenre : subGenre) || '';
  };

  const resetFilterExtendValue = () => {
    resetFilterExtend();
    resetDetailFilterExtend();
  };

  const handleGoGenreFile = (t: string, index: string) => {
    const genreType = index == '0' ? 'main' : 'sub';
    let sourceType = genreSource === 'sensortower' ? 'sensortower' : '';
    let genreResult: any = null;

    if (genreSource === 'appmagic') {
      sourceType = 'appmagic';
      genreResult = {
        genre: t,
        genre_cn: getAppMagicGenreName(t),
        type: 'appmagic',
      };
    } else if (genreSource === 'sensortower') {
      const mainGenreOption = sensorTowerGenreOptions.find((option) =>
        option?.value === (index == '0' ? t : firstMainGenre),
      );
      const targetOption =
        index == '0' ? mainGenreOption : mainGenreOption?.children?.find((option) => option?.value === t);
      genreResult = targetOption
        ? {
            genre: targetOption?.value || t,
            genre_cn: targetOption?.textCN || targetOption?.text || t,
          }
        : null;
    } else {
      const genreMsg = findGenre(t, genreOptions);
      sourceType = genreMsg?.type || sourceType;
      genreResult = genreMsg?.result;
    }

    if (!genreResult?.genre) {
      // return;
      genreResult = {
        genre: t,
        genre_cn: t,
        type: genreSource === 'sensortower' ? 'sensortower' : entityTypeForGenres === 'mobile' ? 'annie' : 'iegg',
      };
    }
    resetFilterExtendValue();
    const genreProfile = `/intelligence/genreProfile/${EntityTypeMsg[entityTypeForGenres]?.path}/detail/insights`;
    const url = `${genreProfile}?entityType=${entityTypeForGenres}&genre=${genreResult?.genre}&genreCn=${genreResult?.genre_cn}&genreType=${genreType}&source=${sourceType}`;
    history.push(url);
  };

  // console.log(companyList)
  // 点击跳转公司详情
  const gotoCompany = (uuid: string) => {
    resetFilterExtendValue();
    history.push(`/intelligence/topCharts/companies/gamesDetails/${uuid}/Summary`);
  };

  return (
    <>
      <div className="dbi-details-header-context" style={{ minHeight: domeHeight }}>
        {companyList?.map((e: any, i: any) => {
          return (
            <React.Fragment key={`company-${i}`}>
              {e?.list?.length > 0 && (
                <div
                  className="dbi-action-tab"
                  key={i}
                  // style={{ marginRight: true ? 20 : 40 }}
                >
                  <div className="dbi-action-tab-title">
                    {i == '0'
                      ? formatMessage({ id: `dbi_publisher` })
                      : formatMessage({ id: `dbi_developer` })}
                  </div>
                  <div className="dbi-action-tab-context">
                    {e?.list
                      ?.filter((item: any) => item.name != '')
                      ?.map((t: any, j: any) => {
                        return t?.companiesOptions ? (
                          <RelationCompanies key={`company-item-${j}`} data={t.companiesOptions} value={t.name} />
                        ) : (
                          <div className="tab-style" key={j} onClick={() => gotoCompany(t?.uuid)}>
                            <TextBubbles text={t.name} />
                          </div>
                        );
                      })}
                    {e?.more?.length > 0 && (
                      <MoreContext
                        data={e?.more}
                        onClick={(e: any) => gotoCompany(e?.uuid)}
                        formatter={(e: any) => e?.name}
                      />
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
        {genreList?.map((e: any, i: any) => {
          const isMainGenre = i === 0;
          return (
            <React.Fragment key={`genre-${i}`}>
              {e?.list?.length > 0 && (
                <div
                  className="dbi-action-tab"
                  key={i}
                  // style={{ marginRight: true ? 20 : 40 }}
                >
                  <div className="dbi-action-tab-title">
                    {i == '0'
                      ? formatMessage({ id: `dbi_main_genre` })
                      : formatMessage({ id: `dbi_sub_genre` })}
                    {i == '0' ? (
                      <TipContext
                        AWidth={420}
                        className="tip-text-hd"
                        tipText={
                          !EntityTypeMsg[entityTypeForGenres]?.mainGenreInfo
                            ? ''
                            : formatMessage({
                                id: EntityTypeMsg[entityTypeForGenres]?.mainGenreInfo,
                              })
                        }
                      />
                    ) : (
                      ''
                    )}
                    {/* {i == '0' ? <TipContext className="tip-text-hd" tipText={EntityTypeMsg[entityTypeForGenres]?.mainGenreInfo ? EntityTypeMsg[entityTypeForGenres]?.mainGenreInfo : ''} /> : ''} */}
                  </div>
                  <div className="dbi-action-tab-context">
                    {e?.list
                      ?.filter((item: any) => item.name != '')
                      ?.map((t: any, j: any) => {
                        return (
                          <div
                            className="tab-style"
                            key={j}
                            onClick={() => handleGoGenreFile(t, i)}
                          >
                            <TextBubbles
                              text={genreFormatMessage({
                                genreSource,
                                isMainGenre,
                                firstMainGenre,
                                genre: t,
                              })}
                            />
                          </div>
                        );
                      })}
                    {e?.more?.length > 0 && (
                      <MoreContext
                        data={e?.more}
                        onClick={(e: string) => handleGoGenreFile(e, i)}
                        formatter={(e: string) =>
                          genreFormatMessage({ genreSource, isMainGenre, firstMainGenre, genre: e })
                        }
                      />
                    )}
                    {/* 品类调整模块 */}
                    <GenreFeedback
                      className="dbi-genre-feedback-btn"
                      entityType={entityType}
                      entityId={basicInfo?.id}
                      entityName={basicInfo?.name}
                      isIegg={basicInfo?.is_iegg}
                      mainGenre={basicInfo?.genre}
                      subGenre={basicInfo?.sub_genre}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}

// 更多
const MoreContext = (props: {
  data: any[];
  formatter?: (payload: any) => string;
  onClick?: (payload: any) => void;
}) => {
  const { data, formatter, onClick = console.log } = props || {};

  const { formatMessage } = useIntl();

  return (
    <Popover
      trigger="click"
      placement="bottom-start"
      overlayClassName="dbi-details-header-popover"
      overlay={
        <DropdownBox>
          <List type="option">
            {data?.map((e: any, i: number) => {
              const text = formatter?.(e) || e;
              return (
                <List.Item key={i} onClick={() => onClick(e)}>
                  {text}
                </List.Item>
              );
            })}
          </List>
        </DropdownBox>
      }
    >
      <div className="tab-style">
        {formatMessage({ id: 'dbi_more' })}
        <SvgIcon type="arrow-down" className="tab-style-icon" />
      </div>
    </Popover>
  );
};

{
  /* 重构用---保留待开发，公司父子组件 */
}
const RelationCompanies = (porps: any) => {
  const { data, value } = porps;
  return (
    <div className="dbi-relation-companies">
      <div className="dbi-relation-companies-style">{value}</div>
      <Popover
        // placementOffset={[-130, 10]}
        placement="bottom-end"
        trigger={'click'}
        overlay={
          <DropdownBox className="dbi-relation-companies-popover">
            {data.map((e: any, i: any) => {
              return (
                <div key={`relation-group-${i}`}>
                  <div className="dbi-relation-companies-group">{e.group}</div>
                  <List type="option">
                    {e.value.map((t: any, j: any) => {
                      return (
                        <React.Fragment key={`relation-item-${i}-${j}`}>
                          <List.Item key={j} className={value == t.text ? 'is-selected' : ''}>
                            <div className="dbi-relation-companies-item">
                              <img className="dbi-relation-companies-img" src={t.img} />
                              <span className="dbi-relation-companies-text">{t.text}</span>
                            </div>
                          </List.Item>
                          {t?.list ? (
                            <List type="option" className="dbi-relation-companies-sublist">
                              {t.list.map((s: any, k: any) => {
                                return (
                                  <List.Item
                                    key={k}
                                    className={value == s.text ? 'is-selected' : ''}
                                  >
                                    <div className="dbi-relation-companies-item">
                                      <img className="dbi-relation-companies-img" src={s.img} />
                                      <span className="dbi-relation-companies-text">{s.text}</span>
                                    </div>
                                  </List.Item>
                                );
                              })}
                            </List>
                          ) : (
                            ''
                          )}
                        </React.Fragment>
                      );
                    })}
                  </List>
                </div>
              );
            })}
          </DropdownBox>
        }
      >
        <div className="dbi-relation-companies-select">
          <SvgIcon type="arrow-down" className="dbi-relation-companies-icon" />
        </div>
      </Popover>
    </div>
  );
};
